<template>
    <modal :open="open" :loading="false" @close="$emit('close')">
        <div class="app-modal-header" slot="header">
            <span>Upload de Fotos</span>
        </div>
        <div class="app-modal-body" slot="body">
            <div class="upload-modal-content">
                <div class="form-row">
                    <label class="app-label" for="select-field">Vincular fotos ao Talhão (obrigatório):</label>
                    <select class="app-select" id="select-field" v-model="selectedFieldId" @change="onFieldChange">
                        <option value="" disabled>Selecione um talhão...</option>
                        <option v-for="f in allFields" :key="f._id" :value="f._id">
                            {{ f.name }}
                        </option>
                    </select>
                </div>

                <div class="form-row mt-2" v-show="selectedFieldId">
                    <label class="app-label" for="select-subarea">Vincular fotos à Subárea (opcional):</label>
                    <select class="app-select" id="select-subarea" v-model="selectedSubareaId">
                        <option value="">Nenhuma</option>
                        <option v-for="sub in fieldSubareas" :key="sub._id" :value="sub._id">
                            {{ sub.seed }}
                        </option>
                    </select>
                </div>

                <div class="upload-queue mt-3" v-show="queue.length > 0">
                    <div class="queue-card" v-for="item in queue" :key="item.id">
                        <div class="queue-card__preview">
                            <img :src="item.preview" alt="Preview"/>
                            <div class="spinner-overlay" v-if="item.uploading">
                                <span class="spinner-border text-white"/>
                            </div>
                            <div class="error-overlay" v-else-if="item.error">
                                <i class="fas fa-exclamation-triangle text-danger fa-2x"/>
                            </div>
                        </div>
                        <div class="queue-card__fields">
                            <div>
                                <label class="app-label">Ponto (Ex: 1, 2, Ponto A):</label>
                                <input class="app-input" type="text" v-model="item.pointName" placeholder="Ex: 83" />
                            </div>
                            <div class="mt-2">
                                <label class="app-label">Observação:</label>
                                <textarea class="app-textarea" v-model="item.notes" rows="2" placeholder="Observações da foto..."/>
                            </div>
                        </div>
                        <button class="queue-card__delete clickable" @click="removeFromQueue(item.id)">
                            <i class="fas fa-trash text-danger"/>
                        </button>
                    </div>
                </div>

                <div class="dropzone mt-3" v-if="queue.length === 0 && selectedFieldId"
                     @dragover.prevent="dragover = true"
                     @dragleave.prevent="dragover = false"
                     @drop.prevent="onDrop"
                     :class="{ 'dropzone--active': dragover }">
                    <input type="file" id="file-input" multiple accept="image/jpeg,image/png" @change="onFileSelect" style="display: none;" />
                    <label for="file-input" class="dropzone__label clickable">
                        <i class="fas fa-cloud-upload fa-2x mb-2 text-teal"/>
                        <span>Arraste fotos ou clique aqui para selecionar</span>
                    </label>
                </div>

                <div class="add-more-container mt-2" v-else-if="queue.length > 0 && selectedFieldId">
                    <input type="file" id="file-input-more" multiple accept="image/jpeg,image/png" @change="onFileSelectMore" style="display: none;" />
                    <button class="btn-add-more clickable" @click="triggerFileInputMore" type="button">
                        <i class="fas fa-plus-circle mr-1"/>
                        <span>Adicionar mais fotos</span>
                    </button>
                </div>
            </div>
        </div>
        <div class="app-modal-footer" slot="footer">
            <button class="btn-sync-submit clickable" v-show="canSubmit" @click="submitPhotos" :disabled="submitting">
                <span class="spinner-border spinner-border-sm" role="status" v-if="submitting" />
                <i class="fas fa-sync-alt" v-else />
            </button>
        </div>
    </modal>
</template>

<script>
import Modal from '@/components/systemUI/Modal'
import PhotoApi from '@/api/Photo'
import subareaApi from "@/api/Subarea"
import { mapGetters } from "vuex"

export default {
    name: 'ModalAddPhotos',
    components: {
        Modal
    },
    props: {
        open: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            selectedFieldId: '',
            selectedSubareaId: '',
            fieldSubareas: [],
            queue: [],
            dragover: false,
            submitting: false
        };
    },
    computed: {
        ...mapGetters("fields", ["allFields"]),
        ...mapGetters("property", ["getPropertySelected", "getCropSelected"]),
        canSubmit() {
            return this.selectedFieldId && this.queue.length > 0 && !this.submitting;
        }
    },
    watch: {
        selectedSubareaId(val) {
            this.fetchPhotosCount();
        },
        getCropSelected(val) {
            this.onFieldChange();
        }
    },
    methods: {
        onFieldChange() {
            this.selectedSubareaId = '';
            this.fieldSubareas = [];
            this.fetchPhotosCount();
            const crop = this.getCropSelected;
            const property = this.getPropertySelected;
            const cropId = crop ? crop._id : '';
            const propertyId = property ? property._id : '';
            if (this.selectedFieldId && cropId && propertyId) {
                subareaApi.findByPropertyAndCrop(propertyId, cropId)
                    .then(subareas => {
                        this.fieldSubareas = subareas.filter((sub) => {
                            if (!sub) return false;
                            const fieldId = sub.field && sub.field._id ? sub.field._id : sub.field;
                            return String(fieldId) === String(this.selectedFieldId);
                        });
                    })
                    .catch(() => {});
            }
        },
        onDrop(e) {
            this.dragover = false;
            const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
            this.processFiles(files);
        },
        onFileSelect(e) {
            const files = Array.from(e.target.files);
            this.processFiles(files);
            e.target.value = '';
        },
        triggerFileInputMore() {
            document.getElementById('file-input-more').click();
        },
        onFileSelectMore(e) {
            const files = Array.from(e.target.files);
            this.processFiles(files);
            e.target.value = '';
        },
        processFiles(files) {
            files.forEach(file => {
                const item = {
                    id: Math.random().toString(36).substring(2),
                    file: file,
                    preview: URL.createObjectURL(file),
                    pointName: '',
                    notes: 'Foto adicionada via web',
                    captured: new Date(),
                    coordinates: { latitude: null, longitude: null },
                    uploading: false,
                    error: false,
                    url: ''
                };
                this.queue.push(item);
                this.extractExif(file, item);
            });
            this.fetchPhotosCount();
        },
        extractExif(file, item) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const arrayBuffer = e.target.result;
                try {
                    const exif = this.readExifData(arrayBuffer);
                    if (exif) {
                        if (exif.captured) {
                            item.captured = exif.captured;
                        }
                        if (exif.latitude !== undefined && exif.longitude !== undefined) {
                            item.coordinates.latitude = exif.latitude;
                            item.coordinates.longitude = exif.longitude;
                        }
                    }
                } catch (err) {}
            };
            reader.readAsArrayBuffer(file);
        },
        readExifData(arrayBuffer) {
            const view = new DataView(arrayBuffer);
            if (view.getUint16(0) !== 0xFFD8) return null;
            
            let length = view.byteLength;
            let offset = 2;
            
            while (offset < length) {
                const marker = view.getUint16(offset);
                if (marker === 0xFFE1) {
                    return this.parseExif(view, offset + 4);
                }
                offset += 2 + view.getUint16(offset + 2);
            }
            return null;
        },
        parseExif(view, offset) {
            if (view.getUint32(offset) !== 0x45786966 || view.getUint16(offset + 4) !== 0) {
                return null;
            }
            
            const tiffOffset = offset + 6;
            const bigEndian = view.getUint16(tiffOffset) === 0x4D4D;
            const get16 = (o) => view.getUint16(o, !bigEndian);
            const get32 = (o) => view.getUint32(o, !bigEndian);
            
            if (get16(tiffOffset + 2) !== 0x002A) return null;
            
            const firstIFDOffset = get32(tiffOffset + 4);
            let ifdOffset = tiffOffset + firstIFDOffset;
            
            const numEntries = get16(ifdOffset);
            let exifIFDOffset = 0;
            let gpsIFDOffset = 0;
            
            for (let i = 0; i < numEntries; i++) {
                const entryOffset = ifdOffset + 2 + i * 12;
                const tag = get16(entryOffset);
                if (tag === 0x8769) {
                    exifIFDOffset = get32(entryOffset + 8);
                } else if (tag === 0x8825) {
                    gpsIFDOffset = get32(entryOffset + 8);
                }
            }
            
            let tags = {};
            
            if (exifIFDOffset) {
                const subIFDOffset = tiffOffset + exifIFDOffset;
                const numSubEntries = get16(subIFDOffset);
                for (let i = 0; i < numSubEntries; i++) {
                    const entryOffset = subIFDOffset + 2 + i * 12;
                    const tag = get16(entryOffset);
                    if (tag === 0x9003) {
                        const count = get32(entryOffset + 4);
                        const valOffset = get32(entryOffset + 8);
                        const absoluteOffset = tiffOffset + valOffset;
                        let dateStr = "";
                        for (let j = 0; j < count - 1; j++) {
                            dateStr += String.fromCharCode(view.getUint8(absoluteOffset + j));
                        }
                        tags.captured = this.parseExifDate(dateStr);
                    }
                }
            }
            
            if (gpsIFDOffset) {
                const gpsOffset = tiffOffset + gpsIFDOffset;
                const numGpsEntries = get16(gpsOffset);
                let lat = null, lng = null;
                let latRef = "N", lngRef = "E";
                
                for (let i = 0; i < numGpsEntries; i++) {
                    const entryOffset = gpsOffset + 2 + i * 12;
                    const tag = get16(entryOffset);
                    
                    if (tag === 1) {
                        latRef = String.fromCharCode(view.getUint8(entryOffset + 8));
                    } else if (tag === 2) {
                        lat = this.parseGPSRational(view, tiffOffset + get32(entryOffset + 8), !bigEndian);
                    } else if (tag === 3) {
                        lngRef = String.fromCharCode(view.getUint8(entryOffset + 8));
                    } else if (tag === 4) {
                        lng = this.parseGPSRational(view, tiffOffset + get32(entryOffset + 8), !bigEndian);
                    }
                }
                
                if (lat !== null && lng !== null) {
                    tags.latitude = latRef === "S" ? -lat : lat;
                    tags.longitude = lngRef === "W" ? -lng : lng;
                }
            }
            
            return tags;
        },
        parseGPSRational(view, offset, littleEndian) {
            const degNum = view.getUint32(offset, littleEndian);
            const degDen = view.getUint32(offset + 4, littleEndian);
            const minNum = view.getUint32(offset + 8, littleEndian);
            const minDen = view.getUint32(offset + 12, littleEndian);
            const secNum = view.getUint32(offset + 16, littleEndian);
            const secDen = view.getUint32(offset + 20, littleEndian);
            
            const degrees = degDen ? (degNum / degDen) : 0;
            const minutes = minDen ? (minNum / minDen) : 0;
            const seconds = secDen ? (secNum / secDen) : 0;
            
            return degrees + (minutes / 60) + (seconds / 3600);
        },
        parseExifDate(dateStr) {
            const parts = dateStr.split(" ");
            if (parts.length === 2) {
                const dateParts = parts[0].split(":");
                const timeParts = parts[1].split(":");
                if (dateParts.length === 3 && timeParts.length === 3) {
                    return new Date(
                        parseInt(dateParts[0], 10),
                        parseInt(dateParts[1], 10) - 1,
                        parseInt(dateParts[2], 10),
                        parseInt(timeParts[0], 10),
                        parseInt(timeParts[1], 10),
                        parseInt(timeParts[2], 10)
                    );
                }
            }
            return null;
        },
        removeFromQueue(id) {
            this.queue = this.queue.filter(item => item.id !== id);
            this.fetchPhotosCount();
        },
        fetchPhotosCount() {
            if (!this.selectedFieldId) {
                this.updateQueuePointNames(0);
                return;
            }
            PhotoApi.findPhotos({
                propertyId: this.getPropertySelected._id,
                fieldId: this.selectedFieldId,
                subareaId: this.selectedSubareaId || ''
            })
            .then(response => {
                const count = (response && response.data && response.data.data) ? response.data.data.length : 0;
                this.updateQueuePointNames(count);
            })
            .catch(() => {
                this.updateQueuePointNames(0);
            });
        },
        updateQueuePointNames(count) {
            this.queue.forEach((item, index) => {
                item.pointName = String(count + index + 1);
            });
        },
        getDefaultCoordinates() {
            const field = this.allFields.find(f => f._id === this.selectedFieldId);
            if (field && field.polygons && field.polygons.length > 0) {
                return {
                    latitude: field.polygons[0].latitude,
                    longitude: field.polygons[0].longitude
                };
            }
            if (this.getPropertySelected && this.getPropertySelected.coordinates) {
                return {
                    latitude: this.getPropertySelected.coordinates.latitude,
                    longitude: this.getPropertySelected.coordinates.longitude
                };
            }
            return { latitude: 0, longitude: 0 };
        },
        async submitPhotos() {
            if (!this.selectedFieldId) {
                return this.$vToastify.error("Por favor selecione um talhão.", "Talhão");
            }
            
            this.submitting = true;

            try {
                const uploadPromises = this.queue.map(async (item) => {
                    if (item.url) return;

                    item.uploading = true;
                    item.error = false;

                    const formData = new FormData();
                    formData.append('file', item.file);

                    try {
                        const response = await PhotoApi.uploadImage(formData);
                        if (response && response.data && response.data.data) {
                            item.url = response.data.data.url;
                        } else {
                            item.error = true;
                            throw new Error("Erro no upload");
                        }
                    } catch (err) {
                        item.error = true;
                        throw err;
                    } finally {
                        item.uploading = false;
                    }
                });

                await Promise.all(uploadPromises);
            } catch (err) {
                this.submitting = false;
                return this.$vToastify.error("Falha ao enviar algumas imagens. Tente novamente.", "Upload");
            }

            const defaultCoords = this.getDefaultCoordinates();
            const photosPayload = this.queue.map(item => {
                const lat = item.coordinates.latitude !== null ? item.coordinates.latitude : defaultCoords.latitude;
                const lng = item.coordinates.longitude !== null ? item.coordinates.longitude : defaultCoords.longitude;
                
                return {
                    field: this.selectedFieldId,
                    subarea: this.selectedSubareaId ? this.selectedSubareaId : null,
                    image: item.url,
                    captured: item.captured.toISOString(),
                    coordinates: {
                        latitude: lat,
                        longitude: lng
                    },
                    pointName: item.pointName,
                    notes: item.notes,
                    active: true
                };
            });

            PhotoApi.create(photosPayload)
                .then(response => {
                    this.submitting = false;
                    this.$vToastify.success("Fotos adicionadas com sucesso!", "Upload");
                    this.$emit('inserted');
                    this.$emit('close');
                    this.resetModal();
                })
                .catch(error => {
                    this.submitting = false;
                    this.$vToastify.error("Erro ao salvar fotos.", "Upload");
                });
        },
        resetModal() {
            this.selectedFieldId = '';
            this.selectedSubareaId = '';
            this.fieldSubareas = [];
            this.queue = [];
        }
    }
}
</script>

<style scoped lang="sass">
.app-modal-body
    width: 100%
    display: flex
    flex-direction: column

.upload-modal-content
    display: flex
    flex-direction: column
    width: 100%
    max-width: 100%
    grid-gap: 12px

.form-row
    display: flex
    flex-direction: column
    grid-gap: 6px

.dropzone
    border: 2px dashed #00808055
    border-radius: 8px
    padding: 24px
    text-align: center
    background: #fbfbfb
    transition: all 0.3s ease

    &--active
        border-color: #008080
        background: #0080800a

    &__label
        display: flex
        flex-direction: column
        align-items: center
        color: #555
        font-size: 14px

.text-teal
    color: #008080

.upload-queue
    max-height: 350px
    overflow-y: auto
    display: flex
    flex-direction: column
    grid-gap: 12px
    padding-right: 4px
    width: 100%

.queue-card
    display: flex
    align-items: flex-start
    border: $border
    border-radius: $border-radius
    padding: 12px
    background: white
    grid-gap: 12px
    position: relative
    width: 100%

    &__preview
        width: 100px
        height: 100px
        position: relative
        border-radius: 4px
        overflow: hidden
        border: $border

        img
            width: 100%
            height: 100%
            object-fit: cover

        .spinner-overlay, .error-overlay
            position: absolute
            top: 0
            left: 0
            width: 100%
            height: 100%
            background: rgba(0, 0, 0, 0.4)
            display: flex
            justify-content: center
            align-items: center

    &__fields
        flex-grow: 1
        display: flex
        flex-direction: column

        .app-input
            width: 100%
            padding: 6px 8px
            font-size: 14px

        .app-textarea
            width: 100%
            padding: 6px 8px
            font-size: 13px
            border-radius: $border-radius
            border: $border
            resize: vertical

    &__delete
        background: none
        border: none
        padding: 4px
        font-size: 16px
        outline: none
        align-self: flex-start

.btn-sync-submit
    display: flex
    justify-content: center
    align-items: center
    width: 50px
    height: 50px
    border-radius: 50%
    border: none
    background-color: #008080
    color: white
    font-size: 18px
    box-shadow: 0 4px 6px rgba(0,0,0,0.1)
    transition: transform 0.2s, background-color 0.2s
    outline: none

    &:hover
        background-color: #006666
        transform: scale(1.05)

    &:disabled
        background-color: #cccccc
        cursor: not-allowed
        transform: none

.add-more-container
    width: 100%
    display: flex
    justify-content: center
    padding: 10px 0

.btn-add-more
    display: flex
    align-items: center
    justify-content: center
    padding: 8px 16px
    border: 1.5px dashed #008080
    background-color: transparent
    color: #008080
    border-radius: 6px
    font-size: 14px
    font-weight: bold
    transition: all 0.2s ease
    outline: none

    &:hover
        background-color: #0080800d
        transform: translateY(-1px)
</style>
